import { Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { FOUNDER_IMAGE_URL } from "@/lib/site";
import { useLang } from "@/i18n/LanguageContext";

export const AboutSection = () => {
  const { t } = useLang();
  const a = t.about;
  return (
    <section id="om" className="relative scroll-mt-24 py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" />
      <div className="relative z-10 mx-auto max-w-7xl px-5">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-[var(--blue)]" />
              <span className="eyebrow">{a.eyebrow}</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1] text-[var(--white)]" data-testid="about-heading">
              {a.title}
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted leading-relaxed" data-testid="about-description">{a.body}</p>
            <div className="mt-8 flex flex-wrap gap-2.5" data-testid="about-stack">
              {a.stack.map((s) => (
                <span key={s} className="inline-flex items-center gap-2 glass rounded-full px-3.5 py-1.5 text-sm text-[var(--white)]">
                  <Check size={13} className="text-[var(--green)]" /> {s}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <div className="relative">
              <div className="glow-orb animate-glow" style={{ width: 360, height: 360, top: "10%", left: "20%", background: "var(--blue)", opacity: 0.3 }} />
              {/* Founder portrait — used as-is, no alterations */}
              <div className="relative glass-card rounded-3xl p-3" data-asset="operator-card">
                <div className="relative overflow-hidden rounded-2xl border border-[var(--line)]">
                  <img
                    src={FOUNDER_IMAGE_URL}
                    alt="Peter Hald, Excel reverse engineering and AI systems specialist"
                    loading="lazy"
                    className="w-full aspect-[4/5] object-cover object-center"
                    data-testid="founder-image"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#05070c] via-[#05070c]/20 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-5">
                    <div className="font-display text-xl font-semibold text-white">Peter Hald</div>
                    <div className="font-mono text-xs text-[var(--blue-bright)]">{a.role}</div>
                  </div>
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 glass rounded-full px-2.5 py-1 font-mono text-[10px] text-[var(--green)]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)] animate-pulse-soft" /> {a.available}
                  </span>
                </div>

                <div className="p-4">
                  <div className="space-y-3 font-mono text-[12px]">
                    {a.meta.map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between">
                        <span className="text-[var(--muted-2)]">{k}</span>
                        <span className="text-[var(--white)]">{v}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 panel rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[var(--green)] text-sm">
                      <span className="h-2 w-2 rounded-full bg-[var(--green)] animate-pulse-soft" />
                      {a.bottom}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
